export const POSITION_COMMAND_TIMEOUT_MS = 15_000;

export class PositionCommandSettlementError extends Error {
  constructor(
    readonly code: 'request_timeout' | 'refresh_timeout' | 'http_error',
    readonly status?: number,
    readonly serverCode?: string,
  ) {
    super(serverCode ?? code);
  }
}

/** Every command phase has a terminal deadline so CommandManager cannot hang. */
export async function settlePositionCommand<T>(
  operation: Promise<T>,
  code: 'request_timeout' | 'refresh_timeout',
  timeoutMs = POSITION_COMMAND_TIMEOUT_MS,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      operation,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new PositionCommandSettlementError(code)), timeoutMs);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export function isPositionCommandConflict(error: unknown): boolean {
  if (!(error instanceof PositionCommandSettlementError) || error.status !== 409) return false;
  if (!error.serverCode) return true;
  return [
    'graph_version_conflict',
    'source_version_conflict',
    'authorization_policy_version_conflict',
    'scenario_revision_conflict',
  ].includes(error.serverCode);
}

/** Retries the same durable intent when the client cannot know the first request's outcome. */
export async function postPositionIntentWithReconciliation(
  post: () => Promise<Response>,
  onIndeterminate: () => void,
  timeoutMs = POSITION_COMMAND_TIMEOUT_MS,
): Promise<Response> {
  for (let attempt = 0; attempt < 4; attempt += 1) {
    let response: Response;
    try {
      response = await settlePositionCommand(post(), 'request_timeout', timeoutMs);
    } catch (error) {
      if (!(error instanceof PositionCommandSettlementError)
        || error.code !== 'request_timeout'
        || attempt === 3) {
        throw error;
      }
      onIndeterminate();
      continue;
    }
    if (response.status !== 409 || attempt === 3) return response;
    const payload = await response.clone().json().catch(() => null) as { error?: string } | null;
    if (payload?.error !== 'intent_in_progress') return response;
    onIndeterminate();
    await new Promise<void>((resolve) => globalThis.setTimeout(resolve, 250 * (attempt + 1)));
  }
  throw new PositionCommandSettlementError('request_timeout');
}
